"use client"
import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import styles from "./PreferredAndForm.module.css"
import autologousMicrograft from "../../assets/images/autologousmicrograft.jpg"
import rhinoplasty from "../../assets/images/rhinoplasty2.jpg"
import sapphireFUE from "../../assets/images/SapphireFUE.png"
import dhiHair from "../../assets/images/dhi-hair.jpg"
import emailjs from '@emailjs/browser';
import { countryCodes } from "../../utils/countryCodes"

function SuccessModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalCheck}>
          <svg width="72" height="72" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="36" cy="36" r="36" fill="#13A672"/><path d="M23 36l10 10 16-16" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <h2 className={styles.modalTitle}>Thank you!</h2>
        <div className={styles.modalMessage}>
          Your request has been received successfully.<br />
          Our medical consultants will contact you shortly to schedule your free consultation.
        </div>
        <button className={styles.modalButton} onClick={onClose}>Close</button>
      </div>
    </div>
  )
}

export default function PreferredAndForm() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [visibleCount, setVisibleCount] = useState(3)
  const [isDragging, setIsDragging] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [dragX, setDragX] = useState(0)
  const [offsetX, setOffsetX] = useState(0) // transient delta from base
  const dragStartXRef = useRef(0)
  const trackRef = useRef(null)
  const containerRef = useRef(null)
  const [tileWidth, setTileWidth] = useState(0)
  const GAP = 16
  const formRef = useRef();
  const [showModal, setShowModal] = useState(false);
  const [isSending, setIsSending] = useState(false)

  // phone with country code (like FixedFormIcon)
  const [selectedCountry, setSelectedCountry] = useState(() => (countryCodes.find(c => c.name === 'United States') || countryCodes[0]))
  const [phone, setPhone] = useState("")
  const [isCodeOpen, setIsCodeOpen] = useState(false)
  const codeWrapRef = useRef(null)
  const navigate = useNavigate()

  const slides = [
    { src: autologousMicrograft, title: "Autologous Micrograft", path: "/autologous-micrograft" },
    { src: rhinoplasty, title: "Rhinoplasty", path: "/rhinoplasty" },
    { src: sapphireFUE, title: "Sapphire FUE Hair Transplant", path: "/sapphire-fue-hair-transplant" },
    { src: dhiHair, title: "DHI Hair Transplant", path: "/dhi-hair-transplant" },
  ]

  // Responsive visible count
  useEffect(() => {
    const updateVisibleCount = () => {
      if (window.innerWidth <= 480) {
        setVisibleCount(1)
      } else if (window.innerWidth <= 768) {
        setVisibleCount(2)
      } else {
        setVisibleCount(3)
      }
    }

    updateVisibleCount()
    window.addEventListener("resize", updateVisibleCount)
    return () => window.removeEventListener("resize", updateVisibleCount)
  }, [])

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length)
  }

  // compute tile width based on container
  useEffect(() => {
    const calc = () => {
      const el = containerRef.current
      if (!el) return
      const w = el.getBoundingClientRect().width
      const per = (w - (visibleCount - 1) * GAP) / visibleCount
      setTileWidth(per)
    }
    calc()
    window.addEventListener("resize", calc)
    return () => window.removeEventListener("resize", calc)
  }, [visibleCount])

  const baseOffset = -(tileWidth + GAP) // hide the leading prev tile

  const getWindowSlides = () => {
    const len = slides.length
    const prevIdx = (currentIndex - 1 + len) % len
    const arr = [slides[prevIdx]]
    for (let i = 0; i < visibleCount; i++) {
      const idx = (currentIndex + i) % len
      arr.push(slides[idx])
    }
    const nextIdx = (currentIndex + visibleCount) % len
    arr.push(slides[nextIdx])
    return arr
  }

  // Drag handlers (pointer events)
  const [wasDragged, setWasDragged] = useState(false) // yeni state ekle

  const onPointerDown = (e) => {
    if (isAnimating) return
    setIsDragging(true)
    setDragX(0)
    setWasDragged(false) // reset drag flag
    dragStartXRef.current = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX) ?? 0
  }

  useEffect(() => {
    if (!isDragging) return
    const onMove = (e) => {
      const clientX = e.clientX ?? (e.touches && e.touches[0] && e.touches[0].clientX)
      if (clientX == null) return
      const delta = clientX - dragStartXRef.current
      setDragX(delta)
      
      // Eğer 5 pikselden fazla hareket ettiyse, bu bir drag işlemi
      if (Math.abs(delta) > 5) {
        setWasDragged(true)
      }
    }
    const onUp = () => {
      const threshold = 50
      const track = trackRef.current
      if (!track) return
      let snapDelta = 0
      if (dragX <= -threshold) {
        // go next
        snapDelta = -(tileWidth + GAP)
        setIsAnimating(true)
        track.style.transition = "transform 300ms ease"
        setOffsetX(snapDelta)
        const onEnd = () => {
          track.style.transition = "none"
          nextSlide()
          setOffsetX(0)
          setIsAnimating(false)
          track.removeEventListener("transitionend", onEnd)
        }
        track.addEventListener("transitionend", onEnd)
      } else if (dragX >= threshold) {
        // go prev
        snapDelta = tileWidth + GAP
        setIsAnimating(true)
        track.style.transition = "transform 300ms ease"
        setOffsetX(snapDelta)
        const onEnd = () => {
          track.style.transition = "none"
          prevSlide()
          setOffsetX(0)
          setIsAnimating(false)
          track.removeEventListener("transitionend", onEnd)
        }
        track.addEventListener("transitionend", onEnd)
      }

      setIsDragging(false)
      setDragX(0)
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("touchmove", onMove, { passive: true })
    window.addEventListener("touchend", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("touchmove", onMove)
      window.removeEventListener("touchend", onUp)
    }
  }, [isDragging, dragX])

  const getVisibleSlides = () => {
    const visible = []
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % slides.length
      visible.push(slides[index])
    }
    return visible
  }

  // Close country code dropdown on outside click
  useEffect(() => {
    if (!isCodeOpen) return
    const onClickOutside = (e) => {
      const el = codeWrapRef.current
      if (el && !el.contains(e.target)) {
        setIsCodeOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('touchstart', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('touchstart', onClickOutside)
    }
  }, [isCodeOpen])

  // EmailJS handler (aligned with FixedFormIcon)
  const sendEmail = (e) => {
    e.preventDefault();
    setIsSending(true)

    const fd = new FormData(formRef.current)
    const params = {
      user_name: fd.get('user_name') || '',
      user_email: fd.get('user_email') || '',
      message: fd.get('message') || '',
      user_phone: fd.get('user_phone') || '',
      country_code: fd.get('country_code') || (selectedCountry?.dial_code || ''),
      user_phone_full: `${fd.get('country_code') || (selectedCountry?.dial_code || '')} ${fd.get('user_phone') || ''}`.trim(),
    }

    emailjs
      .send(
        'service_57xto7k',
        'template_ivxg7n9',
        params,
        { publicKey: 'OPDDEgsIGDGJBTjaz' }
      )
      .then(
        () => {
          setIsSending(false)
          setShowModal(true);
          formRef.current && formRef.current.reset();
          setPhone('')
        },
        (error) => {
          console.error('Email sending failed:', error);
          setIsSending(false)
          alert('There was an error sending your message. Please try again in a short while.');
        },
      );
  };

  return (
    <section className={`section ${styles.white}`}>
      <SuccessModal open={showModal} onClose={() => setShowModal(false)} />
      <div className={`container ${styles.preferredContainer}`}>
        <div className={styles.sliderSection}>
          <h3 className={styles.blockTitle}>Most Preferred</h3>
          <div className={styles.sliderWrapper}>
            <button
              className={`${styles.sliderArrow} ${styles.sliderArrowLeft}`}
              onClick={() => {
                if (isAnimating) return
                const track = trackRef.current
                if (!track) return
                const delta = tileWidth + GAP
                setIsAnimating(true)
                track.style.transition = "transform 300ms ease"
                setOffsetX(delta)
                const onEnd = () => {
                  track.style.transition = "none"
                  prevSlide()
                  setOffsetX(0)
                  setIsAnimating(false)
                  track.removeEventListener("transitionend", onEnd)
                }
                track.addEventListener("transitionend", onEnd)
              }}
              aria-label="Previous"
            >
              ‹
            </button>
            <div
              ref={containerRef}
              className={`${styles.sliderContent} ${styles[`sliderContent-${visibleCount}`]}`}
              onPointerDown={onPointerDown}
              style={{ cursor: isDragging ? "grabbing" : "grab" }}
            >
              <div
                ref={trackRef}
                className={styles.sliderTrack}
                style={{
                  transform: `translateX(${baseOffset + offsetX + dragX}px)`,
                }}
              >
                {getWindowSlides().map((slide, i) => (
                  <div
                    className={styles.sliderTile}
                    key={`${currentIndex}-${i}`}
                    style={{
                      width: tileWidth
                        ? `${tileWidth}px`
                        : `calc((100% - ${(visibleCount - 1) * GAP}px) / ${visibleCount})`,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      // Sadece drag yapılmadıysa yönlendir
                      if (!wasDragged && slide.path) {
                        navigate(slide.path)
                        window.scrollTo(0, 0)
                      }
                    }}
                  >
                    <img src={slide.src || "/placeholder.svg"} alt={slide.title} />
                    <div className={styles.sliderTileCaption}>{slide.title}</div>
                  </div>
                ))}
              </div>
            </div>
            <button
              className={`${styles.sliderArrow} ${styles.sliderArrowRight}`}
              onClick={() => {
                if (isAnimating) return
                const track = trackRef.current
                if (!track) return
                const delta = -(tileWidth + GAP)
                setIsAnimating(true)
                track.style.transition = "transform 300ms ease"
                setOffsetX(delta)
                const onEnd = () => {
                  track.style.transition = "none"
                  nextSlide()
                  setOffsetX(0)
                  setIsAnimating(false)
                  track.removeEventListener("transitionend", onEnd)
                }
                track.addEventListener("transitionend", onEnd)
              }}
              aria-label="Next"
            >
              ›
            </button>
          </div>
        </div>
        <div className={styles.formSection}>
          <div className={styles.formCard} aria-labelledby="form-title">
            <h3 id="form-title" className={styles.formTitle}>
              <span className={styles.formTitleMain}>Register Your Interest</span>
              <br />
              <span className={styles.formTitleSub}>Contact Us For A Free Consultation</span>
            </h3>
            <form
              ref={formRef}
              className={styles.formGrid}
              onSubmit={sendEmail}
            >
              <input type="text" placeholder="Full Name" required name="user_name" />
              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2.8fr', gap: 3 }}>
                <div className={styles.codeSelectWrap} ref={codeWrapRef}>
                  <button
                    type="button"
                    className={styles.codeSelectTrigger}
                    onClick={() => setIsCodeOpen(o => !o)}
                    aria-haspopup="listbox"
                    aria-expanded={isCodeOpen}
                  >
                    <span className={styles.codeLabel}>{selectedCountry?.name || 'United States'}</span>
                    <span className={styles.codeValue}>{selectedCountry?.dial_code || '+1'}</span>
                  </button>
                  {isCodeOpen && (
                    <ul className={styles.codeList} role="listbox">
                      {(() => {
                        const pinnedNames = [
                          'United States',
                          'Russian Federation',
                          'United Kingdom',
                          'Turkey',
                        ]
                        const pinned = countryCodes.filter(c => pinnedNames.includes(c.name))
                        const rest = countryCodes
                          .filter(c => !pinnedNames.includes(c.name))
                          .sort((a, b) => a.name.localeCompare(b.name))
                        return (
                          <>
                            {pinned.map((c) => (
                              <li
                                key={'pinned-' + c.code + c.dial_code}
                                role="option"
                                className={styles.codeItem}
                                onClick={() => { setSelectedCountry(c); setIsCodeOpen(false) }}
                              >
                                <span className={styles.codeLabel}>{c.name}</span>
                                <span className={styles.codeValue}>{c.dial_code}</span>
                              </li>
                            ))}
                            <li className={styles.codeDivider} aria-hidden="true" />
                            {rest.map((c) => (
                              <li
                                key={c.code + c.dial_code}
                                role="option"
                                className={styles.codeItem}
                                onClick={() => { setSelectedCountry(c); setIsCodeOpen(false) }}
                              >
                                <span className={styles.codeLabel}>{c.name}</span>
                                <span className={styles.codeValue}>{c.dial_code}</span>
                              </li>
                            ))}
                          </>
                        )
                      })()}
                    </ul>
                  )}
                  <input type="hidden" name="country_code" value={selectedCountry?.dial_code || ''} />
                </div>
                <input
                  type="tel"
                  placeholder="Phone"
                  required
                  name="user_phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <input type="hidden" name="user_phone_full" value={`${selectedCountry?.dial_code || ''} ${phone}`} />
              <input type="email" placeholder="Email" required name="user_email" />
              <textarea placeholder="Tell us about your goals" rows="3" name="message" />
              <button type="submit" className="primaryBtn" disabled={isSending}>
                {isSending ? 'Sending...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  )
}
